import { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * Measures the active item of a horizontal tab row so an underline can slide
 * to it. Shared by the main tab strip (NavTabs.jsx) and the sub-menu rows
 * (SubNav.jsx) — both mark their buttons with `data-navkey`.
 *
 * The indicator is decoration, not state: the active tab also carries an
 * accent tint, so a row whose measurement never lands (JSDOM, a hidden
 * container, a key that isn't in this row) simply shows no bar.
 *
 * @param {React.RefObject<HTMLElement>} containerRef row element
 * @param {string} activeKey the data-navkey of the current item
 * @param {*} [epoch] any value that should force a re-measure — the row's
 *   ResizeObserver only catches window resizes, not items appearing or
 *   disappearing at a constant container width (a permission change, or the
 *   "More" split moving a tab in or out of the strip).
 * @returns {{left:number,width:number,ready:boolean}} ready is false until the
 *   first successful measure, so the bar fades in where it belongs instead of
 *   animating in from x=0.
 */
export default function useSlidingIndicator(containerRef, activeKey, epoch) {
    const [box, setBox] = useState({ left: 0, width: 0, ready: false });
    //Guards against setState loops: ResizeObserver fires on every layout pass.
    const lastRef = useRef({ left: -1, width: -1 });

    const measure = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;
        const el = activeKey
            ? container.querySelector(`[data-navkey="${CSS.escape(activeKey)}"]`)
            : null;
        //No element means the active item lives somewhere else — inside the
        //"More" dropdown, or a row that doesn't own this key. Collapse the bar.
        if (!el) {
            if (lastRef.current.width !== 0) {
                lastRef.current = { left: 0, width: 0 };
                setBox((prev) => ({ left: prev.left, width: 0, ready: prev.ready }));
            }
            return;
        }
        //offsetLeft rather than getBoundingClientRect: the row can be scrolled
        //(mobile sub-menu), and the indicator scrolls with its content.
        const left = el.offsetLeft;
        const width = el.offsetWidth;
        if (!width) return; //display:none — keep the last good measurement
        if (lastRef.current.left === left && lastRef.current.width === width) return;
        lastRef.current = { left, width };
        setBox({ left, width, ready: true });
        //eslint-disable-next-line react-hooks/exhaustive-deps -- epoch is a
        //re-measure trigger, not a value the measurement reads.
    }, [containerRef, activeKey, epoch]);

    useLayoutEffect(() => {
        measure();
        const container = containerRef.current;
        if (!container) return undefined;

        const observer = new ResizeObserver(measure);
        observer.observe(container);
        //Tab widths depend on text, and Geist loads with font-display: swap —
        //the first measurement is taken in the fallback font.
        let cancelled = false;
        document.fonts?.ready.then(() => { if (!cancelled) measure(); });

        return () => { cancelled = true; observer.disconnect(); };
    }, [measure, containerRef]);

    return box;
}
